/**
 * Ability VFX — particle bursts spawned at the caster (or victim) when
 * an ability fires. Cheap: one ParticleSystem per cast, auto-dispose
 * after the burst.
 *
 * Each ability id is mapped to one of a handful of "elements" so a new
 * ability automatically picks up a sensible default visual.
 */

import {
  Color3,
  Color4,
  GlowLayer,
  type Mesh,
  MeshBuilder,
  ParticleSystem,
  type Scene,
  StandardMaterial,
  Texture,
  Vector3,
} from '@babylonjs/core';

// Single shared particle texture. `flare.png` is a Babylon CDN classic;
// we point at a local data URI so no network hit. 16x16 white radial blob.
let cachedTex: Texture | null = null;
function tex(scene: Scene): Texture {
  if (cachedTex) return cachedTex;
  // Inline 16x16 radial gradient PNG, white-on-transparent.
  cachedTex = new Texture(
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAdklEQVQ4y2NkAAGGyclEAQYG0v///+9CkD8QwxCBcEYUgwYBeYwR5oRRJjATBAxoQGcr1FFfgY9hABgYRwIzYwIc0ABwYIaiBHsVJEBKf0xC0mgKIEmKZCFAvk0CYBUjBXwBA8MwgB6BSDAqMCowKjAqMCowQEgADQYHzkLLHbcAAAAASUVORK5CYII=',
    scene,
    true,
    false,
  );
  return cachedTex;
}

type Element = 'fire' | 'frost' | 'holy' | 'shadow' | 'arrow' | 'slash' | 'nature';

const ABILITY_ELEMENT: Record<string, Element> = {
  // mage
  'firebolt': 'fire',
  'fire-blast': 'fire',
  'meteor': 'fire',
  'frost-nova': 'frost',
  'ice-lance': 'frost',
  'arcane-missile': 'holy',
  'arcane-blast': 'holy',
  // cleric
  'heal': 'holy',
  'greater-heal': 'holy',
  'smite': 'holy',
  'judgement': 'holy',
  'sanctuary': 'holy',
  // ranger
  'arrow-shot': 'arrow',
  'multishot': 'arrow',
  'leap': 'nature',
  'snare-trap': 'nature',
  'rain-of-arrows': 'arrow',
  // warrior
  'cleave': 'slash',
  'charge': 'slash',
  'guard': 'holy',
  'taunt': 'shadow',
  'whirlwind': 'slash',
};

function elementFor(abilityId: string): Element {
  if (ABILITY_ELEMENT[abilityId]) return ABILITY_ELEMENT[abilityId];
  if (/heal|mend|restore|holy/i.test(abilityId)) return 'holy';
  if (/fire|flame|burn|meteor/i.test(abilityId)) return 'fire';
  if (/frost|ice|cold|chill/i.test(abilityId)) return 'frost';
  if (/arrow|shot|aim/i.test(abilityId)) return 'arrow';
  if (/slash|cleave|charge|whirl|sweep/i.test(abilityId)) return 'slash';
  if (/snare|root|nature/i.test(abilityId)) return 'nature';
  return 'shadow';
}

const PALETTE: Record<Element, [Color4, Color4]> = {
  fire:   [new Color4(1.0, 0.6, 0.10, 1), new Color4(1.0, 0.20, 0.05, 0)],
  frost:  [new Color4(0.6, 0.85, 1.0, 1), new Color4(0.3, 0.55, 1.0, 0)],
  holy:   [new Color4(1.0, 0.95, 0.55, 1), new Color4(1.0, 1.0, 0.85, 0)],
  shadow: [new Color4(0.55, 0.20, 0.65, 1), new Color4(0.10, 0.05, 0.20, 0)],
  arrow:  [new Color4(0.85, 0.85, 0.50, 1), new Color4(0.30, 0.20, 0.05, 0)],
  slash:  [new Color4(1.0, 0.95, 0.95, 1), new Color4(0.95, 0.85, 0.45, 0)],
  nature: [new Color4(0.50, 0.95, 0.45, 1), new Color4(0.10, 0.45, 0.10, 0)],
};

export interface SpawnVfxOpts {
  scene: Scene;
  position: Vector3;
  abilityId: string;
}

export function spawnAbilityVFX({ scene, position, abilityId }: SpawnVfxOpts): void {
  const elt = elementFor(abilityId);
  const [c1, c2] = PALETTE[elt];

  const ps = new ParticleSystem(`vfx-${abilityId}`, 90, scene);
  ps.particleTexture = tex(scene);
  ps.emitter = position.add(new Vector3(0, 1.2, 0));
  ps.minEmitBox = new Vector3(-0.2, 0, -0.2);
  ps.maxEmitBox = new Vector3(0.2, 0.4, 0.2);
  ps.color1 = c1;
  ps.color2 = c2;
  ps.colorDead = new Color4(c2.r, c2.g, c2.b, 0);
  ps.minSize = 0.18;
  ps.maxSize = 0.55;
  ps.minLifeTime = 0.35;
  ps.maxLifeTime = 0.85;
  ps.emitRate = 220;
  ps.blendMode = ParticleSystem.BLENDMODE_ADD;
  ps.gravity = elt === 'fire' || elt === 'holy'
    ? new Vector3(0, 4, 0)
    : new Vector3(0, -2, 0);
  ps.direction1 = new Vector3(-1.5, 2, -1.5);
  ps.direction2 = new Vector3(1.5, 4, 1.5);
  ps.minAngularSpeed = 0;
  ps.maxAngularSpeed = Math.PI;
  ps.minEmitPower = 1;
  ps.maxEmitPower = 3;
  ps.updateSpeed = 0.012;

  // Specific element tweaks.
  if (elt === 'frost') {
    ps.gravity = new Vector3(0, 0.3, 0);
    ps.minLifeTime = 0.6;
    ps.maxLifeTime = 1.2;
  }
  if (elt === 'holy') {
    ps.minSize = 0.25;
    ps.maxSize = 0.7;
  }
  if (elt === 'slash') {
    ps.direction1 = new Vector3(-3, 1, -1);
    ps.direction2 = new Vector3(3, 1.5, 1);
    ps.minLifeTime = 0.18;
    ps.maxLifeTime = 0.32;
  }
  if (elt === 'arrow') {
    ps.direction1 = new Vector3(-0.5, 0.2, -2);
    ps.direction2 = new Vector3(0.5, 0.5, 2);
    ps.minLifeTime = 0.18;
    ps.maxLifeTime = 0.4;
    ps.minEmitPower = 4;
    ps.maxEmitPower = 8;
  }

  ps.start();
  // Stop emitting after 200 ms; live particles still finish their lifetime.
  setTimeout(() => ps.stop(), 200);
  // Final disposal once all alive particles are dead.
  setTimeout(() => ps.dispose(), 1600);
}

/**
 * Class-specific projectile that flies from caster to target. On arrival
 * it spawns an impact burst at the target. Used for fireball / frostbolt /
 * arrow / arcane missile.
 */
export interface SpawnProjectileOpts {
  scene: Scene;
  from: Vector3;
  toResolver: () => Vector3 | null; // re-evaluated each frame so target tracks moving mobs
  abilityId: string;
}

export function spawnProjectileVFX({ scene, from, toResolver, abilityId }: SpawnProjectileOpts): void {
  const elt = elementFor(abilityId);
  const [c1] = PALETTE[elt];

  const projColour = new Color3(c1.r, c1.g, c1.b);
  const ball = MeshBuilder.CreateSphere(`proj-${abilityId}-${Math.random().toString(36).slice(2, 6)}`, { diameter: elt === 'arrow' ? 0.3 : 0.6, segments: 10 }, scene);
  const mat = new StandardMaterial(`${ball.name}-mat`, scene);
  mat.diffuseColor = projColour;
  mat.emissiveColor = projColour;
  mat.specularColor = new Color3(0, 0, 0);
  ball.material = mat;
  ball.isPickable = false;
  ball.position.copyFrom(from);
  ball.position.y += 1.2;

  // Trailing particle wake.
  const trail = new ParticleSystem(`${ball.name}-trail`, 60, scene);
  trail.particleTexture = tex(scene);
  trail.emitter = ball as Mesh;
  trail.minEmitBox = new Vector3(-0.06, -0.06, -0.06);
  trail.maxEmitBox = new Vector3(0.06, 0.06, 0.06);
  trail.color1 = c1;
  trail.color2 = new Color4(c1.r, c1.g, c1.b, 0);
  trail.minSize = 0.15;
  trail.maxSize = 0.35;
  trail.minLifeTime = 0.18;
  trail.maxLifeTime = 0.32;
  trail.emitRate = 140;
  trail.blendMode = ParticleSystem.BLENDMODE_ADD;
  trail.gravity = new Vector3(0, 0, 0);
  trail.direction1 = new Vector3(-0.4, -0.4, -0.4);
  trail.direction2 = new Vector3(0.4, 0.4, 0.4);
  trail.minEmitPower = 0.4;
  trail.maxEmitPower = 1.2;
  trail.start();

  const speed = elt === 'arrow' ? 36 : elt === 'frost' ? 22 : 28;
  let lastTime = performance.now();
  const obs = scene.onBeforeRenderObservable.add(() => {
    const now = performance.now();
    const dt = (now - lastTime) / 1000;
    lastTime = now;
    const target = toResolver();
    if (!target) {
      // Target gone — fizzle.
      trail.stop();
      setTimeout(() => { trail.dispose(); ball.dispose(); mat.dispose(); }, 400);
      scene.onBeforeRenderObservable.remove(obs);
      return;
    }
    const tx = target.x;
    const tz = target.z;
    const ty = (target.y || 0) + 1.2;
    const dx = tx - ball.position.x;
    const dy = ty - ball.position.y;
    const dz = tz - ball.position.z;
    const d = Math.hypot(dx, dy, dz);
    if (d < 0.6) {
      // Impact!
      spawnAbilityVFX({ scene, position: new Vector3(tx, target.y || 0, tz), abilityId });
      trail.stop();
      setTimeout(() => { trail.dispose(); ball.dispose(); mat.dispose(); }, 600);
      scene.onBeforeRenderObservable.remove(obs);
      return;
    }
    const step = Math.min(d, speed * dt);
    ball.position.x += (dx / d) * step;
    ball.position.y += (dy / d) * step;
    ball.position.z += (dz / d) * step;
  });

  // Hard cap — kill the projectile after 3s no matter what (stuck-fire safety).
  setTimeout(() => {
    if (!ball.isDisposed()) {
      trail.stop();
      ball.dispose(); mat.dispose();
      scene.onBeforeRenderObservable.remove(obs);
    }
  }, 3000);
  void GlowLayer;
}

// ── R232q5d: Combat VFX library — blood / dust / impact ───────────────

/**
 * Blood splatter when a melee hit lands. Quick burst of dark red particles
 * fanning outward + a low-arc spray. Auto-dispose 0.4s.
 */
export function spawnBloodSplatter(scene: Scene, pos: Vector3): void {
  const ps = new ParticleSystem(`blood-${Date.now()}-${Math.random().toString(36).slice(2,5)}`, 36, scene);
  ps.particleTexture = tex(scene);
  ps.emitter = pos;
  ps.minEmitBox = new Vector3(-0.1, 0.5, -0.1);
  ps.maxEmitBox = new Vector3(0.1, 0.9, 0.1);
  ps.color1 = new Color4(0.55, 0.10, 0.10, 1);
  ps.color2 = new Color4(0.30, 0.05, 0.05, 1);
  ps.colorDead = new Color4(0.10, 0.02, 0.02, 0);
  ps.minSize = 0.10;
  ps.maxSize = 0.22;
  ps.minLifeTime = 0.30;
  ps.maxLifeTime = 0.55;
  ps.emitRate = 220;
  ps.gravity = new Vector3(0, -8, 0);
  ps.direction1 = new Vector3(-1.4, 0.6, -1.4);
  ps.direction2 = new Vector3(1.4, 1.6, 1.4);
  ps.minEmitPower = 1.5;
  ps.maxEmitPower = 3.5;
  ps.start();
  setTimeout(() => ps.stop(), 60);
  setTimeout(() => ps.dispose(), 700);
}

/**
 * Footstep dust — soft puff under the player's feet when running on dry
 * surfaces. Cheap (8 particles, ~250ms life).
 */
export function spawnFootstepDust(scene: Scene, pos: Vector3, surfaceTint: 'grass' | 'sand' | 'snow' | 'stone' = 'grass'): void {
  const ps = new ParticleSystem(`dust-${Date.now()}-${Math.random().toString(36).slice(2,5)}`, 8, scene);
  ps.particleTexture = tex(scene);
  ps.emitter = pos;
  ps.minEmitBox = new Vector3(-0.15, 0.02, -0.15);
  ps.maxEmitBox = new Vector3(0.15, 0.08, 0.15);
  const tints: Record<string, [Color4, Color4]> = {
    grass: [new Color4(0.50, 0.42, 0.25, 0.55), new Color4(0.35, 0.30, 0.18, 0.0)],
    sand:  [new Color4(0.85, 0.72, 0.45, 0.55), new Color4(0.65, 0.55, 0.35, 0.0)],
    snow:  [new Color4(0.95, 0.97, 1.00, 0.65), new Color4(0.85, 0.90, 0.95, 0.0)],
    stone: [new Color4(0.55, 0.55, 0.55, 0.55), new Color4(0.40, 0.40, 0.40, 0.0)],
  };
  const [c1, c2] = tints[surfaceTint] ?? tints.grass;
  ps.color1 = c1;
  ps.color2 = c1;
  ps.colorDead = c2!;
  ps.minSize = 0.18;
  ps.maxSize = 0.32;
  ps.minLifeTime = 0.20;
  ps.maxLifeTime = 0.40;
  ps.emitRate = 60;
  ps.gravity = new Vector3(0, 0.4, 0);
  ps.direction1 = new Vector3(-0.8, 0.6, -0.8);
  ps.direction2 = new Vector3(0.8, 1.4, 0.8);
  ps.minEmitPower = 0.4;
  ps.maxEmitPower = 1.0;
  ps.start();
  setTimeout(() => ps.stop(), 40);
  setTimeout(() => ps.dispose(), 600);
}

/**
 * Weapon swing trail — bright arc that follows a melee swing for ~200ms.
 * Used by selfRig.triggerAttack for visible feedback.
 */
export function spawnSwingTrail(scene: Scene, pos: Vector3, tint: 'steel' | 'fire' | 'frost' | 'shadow' = 'steel'): void {
  const ps = new ParticleSystem(`swing-${Date.now()}-${Math.random().toString(36).slice(2,5)}`, 32, scene);
  ps.particleTexture = tex(scene);
  ps.emitter = pos;
  ps.minEmitBox = new Vector3(-0.05, 0.9, -0.05);
  ps.maxEmitBox = new Vector3(0.05, 1.4, 0.05);
  const tints: Record<string, [Color4, Color4]> = {
    steel:  [new Color4(0.85, 0.92, 1.00, 0.85), new Color4(0.50, 0.60, 0.80, 0)],
    fire:   [new Color4(1.00, 0.55, 0.10, 0.9), new Color4(0.55, 0.10, 0.0, 0)],
    frost:  [new Color4(0.55, 0.85, 1.00, 0.9), new Color4(0.20, 0.35, 0.55, 0)],
    shadow: [new Color4(0.45, 0.20, 0.55, 0.85), new Color4(0.15, 0.05, 0.20, 0)],
  };
  const [c1, c2] = tints[tint] ?? tints.steel;
  ps.color1 = c1;
  ps.color2 = c1;
  ps.colorDead = c2!;
  ps.minSize = 0.08;
  ps.maxSize = 0.18;
  ps.minLifeTime = 0.10;
  ps.maxLifeTime = 0.22;
  ps.emitRate = 240;
  ps.gravity = new Vector3(0, -1.5, 0);
  ps.direction1 = new Vector3(-1.5, 0.2, -0.5);
  ps.direction2 = new Vector3(1.5, 1.2, 0.5);
  ps.minEmitPower = 1.0;
  ps.maxEmitPower = 2.5;
  ps.start();
  setTimeout(() => ps.stop(), 60);
  setTimeout(() => ps.dispose(), 350);
}

/**
 * Generic damage tick — small white spark used for crit/strong hits.
 */
export function spawnImpactSpark(scene: Scene, pos: Vector3): void {
  const ps = new ParticleSystem(`impact-${Date.now()}-${Math.random().toString(36).slice(2,5)}`, 24, scene);
  ps.particleTexture = tex(scene);
  ps.emitter = pos;
  ps.minEmitBox = new Vector3(-0.05, 0.9, -0.05);
  ps.maxEmitBox = new Vector3(0.05, 1.1, 0.05);
  ps.color1 = new Color4(1.00, 0.95, 0.55, 1);
  ps.color2 = new Color4(1.00, 1.00, 0.85, 1);
  ps.colorDead = new Color4(0.85, 0.55, 0.20, 0);
  ps.minSize = 0.06;
  ps.maxSize = 0.14;
  ps.minLifeTime = 0.18;
  ps.maxLifeTime = 0.30;
  ps.emitRate = 200;
  ps.gravity = new Vector3(0, -3, 0);
  ps.direction1 = new Vector3(-2, 0.8, -2);
  ps.direction2 = new Vector3(2, 2.4, 2);
  ps.minEmitPower = 1.5;
  ps.maxEmitPower = 3.0;
  ps.start();
  setTimeout(() => ps.stop(), 50);
  setTimeout(() => ps.dispose(), 500);
}
